import { api } from "@/redux/api";

import {
	CreateOutreachInstructionTemplate,
	OutreachInstructionTemplate,
	OutreachInstructionTemplateListSchema,
	OutreachInstructionTemplateSchema,
	OutreachTemplateKind,
	UpdateOutreachInstructionTemplate,
} from "../../pages/List/schema/outreach-instruction-template.schema";

/**
 * Which shelf of the library to read: a `COPYWRITING` library is per list source type, a
 * `SENDER_ROUTING` one is company-wide and passes `listSourceType: null`.
 */
export interface OutreachTemplateScopeArgs {
	kind: OutreachTemplateKind;
	listSourceType: string | null;
}

export interface UpdateOutreachInstructionTemplateArgs {
	id: number;
	body: UpdateOutreachInstructionTemplate;
}

/**
 * The company's saved outreach instructions (PREC-3687 §6).
 *
 * Its own `injectEndpoints` file because `list.api.ts` is at the file-length ratchet ceiling and
 * may not grow; RTK Query injects into the same base `api` across files
 * (cf. `list-outreach.api.ts`).
 *
 * Components must not read the query hook directly — payloads are mirrored into
 * `list-outreach-template.slice.ts`, bucketed by scope, and read from its selector
 * (apps/web CLAUDE.md §2).
 */
export const listOutreachTemplateApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getOutreachInstructionTemplates: builder.query<
			OutreachInstructionTemplate[],
			OutreachTemplateScopeArgs
		>({
			query: ({ kind, listSourceType }) => ({
				url: "outreach-instruction-template",
				// Omitted rather than sent as null: the API rejects a SENDER_ROUTING request that
				// carries a source type, because scoping company-wide rules would hide them.
				params: listSourceType ? { kind, listSourceType } : { kind },
				responseSchema: OutreachInstructionTemplateListSchema,
			}),
			providesTags: ["OutreachInstructionTemplate"],
		}),
		createOutreachInstructionTemplate: builder.mutation<
			OutreachInstructionTemplate,
			CreateOutreachInstructionTemplate
		>({
			query: (body) => ({
				url: "outreach-instruction-template",
				method: "POST",
				body,
				responseSchema: OutreachInstructionTemplateSchema,
			}),
			// A create may take over the scope's default, so the whole library is stale, not one row.
			invalidatesTags: ["OutreachInstructionTemplate"],
		}),
		updateOutreachInstructionTemplate: builder.mutation<
			OutreachInstructionTemplate,
			UpdateOutreachInstructionTemplateArgs
		>({
			query: ({ id, body }) => ({
				url: `outreach-instruction-template/${id}`,
				method: "PATCH",
				body,
				responseSchema: OutreachInstructionTemplateSchema,
			}),
			invalidatesTags: ["OutreachInstructionTemplate"],
		}),
		deleteOutreachInstructionTemplate: builder.mutation<OutreachInstructionTemplate, number>({
			query: (id) => ({
				url: `outreach-instruction-template/${id}`,
				method: "DELETE",
				responseSchema: OutreachInstructionTemplateSchema,
			}),
			invalidatesTags: ["OutreachInstructionTemplate"],
		}),
	}),
});

export const {
	useGetOutreachInstructionTemplatesQuery,
	useCreateOutreachInstructionTemplateMutation,
	useUpdateOutreachInstructionTemplateMutation,
	useDeleteOutreachInstructionTemplateMutation,
} = listOutreachTemplateApi;
